import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '../components/ui';
import { useAuthStore } from '../stores';
import { loginSchema, type LoginFormData } from '../utils/validation';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const from = (location.state as { from?: Location })?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data);
      toast.success('Welcome back!');

      // Sysadmin users go to admin panel by default
      const authState = useAuthStore.getState();
      if (authState.user?.isSuperAdmin) {
        navigate('/admin/users-roles', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      toast.error(error || 'Login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-secondary-900">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CH</span>
              </div>
              <span className="font-display text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Capture Health
              </span>
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Welcome back</h2>
            <p className="mt-2 text-secondary-600 dark:text-secondary-400">
              Sign in to access your client portal
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              leftIcon={<EnvelopeIcon className="h-5 w-5" />}
              error={errors.email?.message}
              {...register('email')}
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                }
                error={errors.password?.message}
                {...register('password')}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-500 focus:ring-primary-500 dark:bg-secondary-800"
                />
                <span className="ml-2 text-sm text-secondary-600 dark:text-secondary-400">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-500 hover:text-primary-600"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              Sign in
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-secondary-600 dark:text-secondary-400">
            Don't have an account?{' '}
            <Link to="/signup" className="font-medium text-primary-500 hover:text-primary-600">
              Register your business
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:block relative flex-1 gradient-mesh bg-secondary-50 dark:bg-secondary-800">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-center">
            <div className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-white dark:bg-secondary-700 shadow-elevated flex items-center justify-center">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">CH</span>
              </div>
            </div>
            <h3 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
              Manage Your Telehealth Business
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Access orders, manage patients, track medications, and streamline your operations
              all in one powerful platform.
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <div className="px-4 py-2 bg-white/80 dark:bg-secondary-700/80 backdrop-blur-sm rounded-lg shadow-soft">
                <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-50">10k+</p>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">Orders</p>
              </div>
              <div className="px-4 py-2 bg-white/80 dark:bg-secondary-700/80 backdrop-blur-sm rounded-lg shadow-soft">
                <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-50">5k+</p>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">Patients</p>
              </div>
              <div className="px-4 py-2 bg-white/80 dark:bg-secondary-700/80 backdrop-blur-sm rounded-lg shadow-soft">
                <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-50">99.9%</p>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">Uptime</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;



